import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { useMember, memberPhotoUrl } from '@/lib/use-session';
import {
  CircleDetailView,
  type CircleJoinRequest,
  type CircleTabId,
  type CircleViewerRole,
} from '@/components/circles/CircleDetailView';
import {
  adaptEventSummary,
  type EventCardData,
} from '@/components/events/event-adapters';
import {
  groupKeys,
  type CircleDetailResponse,
  type GroupDetail,
} from '@/lib/queries/groups';
import { useFeed } from '@/lib/queries/feed';

const VALID_TABS: ReadonlyArray<CircleTabId> = [
  'overview',
  'feed',
  'members',
  'events',
  'resources',
];

type Search = { tab?: CircleTabId };

function toTabId(s: unknown): CircleTabId {
  if (typeof s === 'string' && (VALID_TABS as ReadonlyArray<string>).includes(s)) {
    return s as CircleTabId;
  }
  return 'overview';
}

export const Route = createFileRoute('/_authed/circles/$id/')({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    tab: toTabId(raw.tab),
  }),
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: CircleDetailPage,
});

// Top-level route component. Fires only the access-determining query, then
// renders CircleDetailView for all access levels — visitor, member, leader.
// Visitors receive null membersData/feedItems/upcomingMeetings; member-only
// queries are never fired for them.
function CircleDetailPage() {
  const { id } = Route.useParams();

  const detailQuery = useQuery(groupKeys.circle.detail(id));
  const data: CircleDetailResponse | undefined = detailQuery.data;

  if (isNotFoundApiError(detailQuery.error)) return <NotFoundPage />;
  if (detailQuery.isError && !detailQuery.data) throw notFound();

  if (data === undefined) {
    return <CircleDetailLoadingShell id={id} />;
  }

  if (data.access === 'visitor') {
    return <CircleVisitorDetail id={id} detail={data} />;
  }

  return (
    <AuthedCircleDetail
      id={id}
      circle={data.circle}
      access={data.access}
    />
  );
}

function CircleDetailLoadingShell({ id }: { id: string }) {
  const me = useMember();
  if (me === null) return null;
  return (
    <CircleDetailView
      circleId={id}
      detail={null}
      membersData={null}
      feedItems={null}
      upcomingMeetings={null}
      conversationId={undefined}
      viewerRole="member"
      viewer={{
        id: me.id,
        name: `${me.firstName} ${me.lastName}`.trim(),
        photoUrl: memberPhotoUrl(me) ?? null,
      }}
    />
  );
}

function CircleVisitorDetail({
  id,
  detail,
}: {
  readonly id: string;
  readonly detail: Extract<CircleDetailResponse, { access: 'visitor' }>;
}) {
  const me = useMember();
  if (me === null) return null;
  return (
    <CircleDetailView
      circleId={id}
      detail={detail.circle}
      membersData={null}
      feedItems={null}
      upcomingMeetings={null}
      conversationId={undefined}
      viewerRole={null}
      viewer={{
        id: me.id,
        name: `${me.firstName} ${me.lastName}`.trim(),
        photoUrl: memberPhotoUrl(me) ?? null,
      }}
    />
  );
}

// Member / leader experience. Mounts only when access is known — visitors
// never pay for these queries.
function AuthedCircleDetail({
  id,
  circle,
  access,
}: {
  readonly id: string;
  readonly circle: GroupDetail;
  readonly access: 'member' | 'leader';
}) {
  const me = useMember();
  const isLeader = access === 'leader';
  const viewerRole: CircleViewerRole = isLeader ? 'leader' : 'member';

  const membersQuery = useQuery(groupKeys.members(id, { pageSize: 50 }));
  const feedQuery = useFeed({ scope: `group:${id}`, pageSize: 30 });
  const eventsQuery = useQuery(
    groupKeys.circle.events(id, { window: 'upcoming', pageSize: 25 }),
  );
  const conversationQuery = useQuery(groupKeys.circle.conversation(id));
  const joinRequestsQuery = useQuery({
    ...groupKeys.circle.joinRequests(id),
    enabled: isLeader,
  });
  const members = membersQuery.data?.items ?? null;
  const feedItems = feedQuery.data?.items ?? null;
  const conversation = conversationQuery.data;

  const upcomingMeetings: EventCardData[] | null = (() => {
    const items = eventsQuery.data?.items;
    if (items === undefined) return null;
    return items
      .filter((e) => e.cancelledAt === null)
      .slice()
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
      .map((e) => adaptEventSummary(e));
  })();

  const joinRequests: CircleJoinRequest[] = isLeader
    ? (joinRequestsQuery.data?.items ?? []).map((r) => ({
        id: r.id,
        userId: r.userId,
        name:
          r.member !== null
            ? `${r.member.firstName} ${r.member.lastName}`.trim()
            : 'Member',
        photoUrl: r.member?.photoUrl ?? null,
        jobTitle: r.member?.jobTitle ?? null,
        company: r.member?.company ?? null,
        message: r.message,
        createdAt: r.createdAt,
      }))
    : [];

  if (me === null) return null;

  return (
    <CircleDetailView
      circleId={id}
      detail={circle}
      membersData={members}
      feedItems={feedItems}
      upcomingMeetings={upcomingMeetings}
      conversationId={
        conversation === undefined ? undefined : conversation?.id ?? null
      }
      viewer={{
        id: me.id,
        name: `${me.firstName} ${me.lastName}`.trim(),
        photoUrl: memberPhotoUrl(me) ?? null,
      }}
      viewerRole={viewerRole}
      joinRequests={joinRequests}
    />
  );
}
